/** Single source of truth for user-facing verdict / action / badge display. */
import { tradingDaysBetween } from "./scanLedger.js";
import {
  attachScannerAction,
  isScoringV2Result,
  mapScannerActionToDisplay,
  normalizeScanResult,
} from "./resolveScannerAction.js";

const SUBTITLE_BY_VERDICT = {
  "⚡ CONSIDER CALLS": "Setup confirmed — consider entry",
  "👁 WATCH": "Setup building — monitor",
  "👁 WATCH — BEARISH": "Weakness building — monitor",
  "⚪ WAIT": "No edge — stay in cash",
  "⚠ WAIT — EXTENDED": "Extended — wait for pullback to EMA21",
  "⚠ TOP FORMING": "Distribution detected — avoid longs",
  "🔵 BOTTOM CONFIRMED": "Reversal setup — watch for entry",
  "⚠ CONSIDER PUTS": "Breakdown confirmed — consider puts",
};

function normalizeSubtitle(verdict, subtitle) {
  return SUBTITLE_BY_VERDICT[verdict] ?? subtitle;
}

function actionTextForVerdict(verdict) {
  if (verdict === "⚡ CONSIDER CALLS") return "✅ CONSIDER CALLS";
  if (verdict === "⚠ CONSIDER PUTS") return "✅ CONSIDER PUTS";
  if (verdict === "⚠ WAIT — EXTENDED") return "👁 WAIT — PULLBACK";
  if (verdict === "👁 WATCH — BEARISH") return "⚠ WATCH PUTS";
  if (verdict === "👁 WATCH") return "👁 WATCH";
  if (verdict === "⚠ TOP FORMING") return "EXIT / SHORT";
  if (verdict === "🔵 BOTTOM CONFIRMED") return "👁 WATCH — BUILDING";
  if (verdict === "⚪ WAIT") return "—";
  return "—";
}

function baseDisplay(scanResult, opts) {
  const score =
    typeof scanResult?.buyScore === "number"
      ? scanResult.buyScore
      : typeof scanResult?.score === "number"
      ? scanResult.score
      : null;
  return {
    verdict: "⚪ WAIT",
    verdictStyle: "neutral",
    subtitle: SUBTITLE_BY_VERDICT["⚪ WAIT"],
    actionText: actionTextForVerdict("⚪ WAIT"),
    showCallsBadge: false,
    showPutsBadge: false,
    holdingText: null,
    showExitSignal: false,
    exitLabel: null,
    scoreDisplay: score,
    signalStrength: scanResult?.signalStrength ?? null,
    _opts: opts,
  };
}

/**
 * @param {object} scanResult Full ticker scan result
 * @param {{ inPosition?: boolean, activeSignal?: object }} [opts]
 */
export function resolveDisplayVerdict(scanResult, opts = {}) {
  const result = scanResult || {};
  normalizeScanResult(result);
  const inPosition = opts.inPosition === true;
  const activeSignal = opts.activeSignal ?? result.activeSignal ?? null;

  if (isScoringV2Result(result)) {
    let out = mapScannerActionToDisplay(result, opts);
    return finalizePositionState(out, inPosition, activeSignal);
  }

  const score = typeof result.buyScore === "number" ? result.buyScore : null;
  const moduleSignal = result.signal;
  const pm = result.postMoveAssessment;
  const move30d = result.move21d;
  const move7d = result.move5d;
  const pctFromHigh =
    typeof result.pctFromRecentHigh === "number" ? result.pctFromRecentHigh : null;
  const nearHighs = pctFromHigh != null && pctFromHigh >= -5;

  let out = baseDisplay(result, opts);
  out.scoreDisplay = score;

  // ── RULE 1 — Post-move override ──
  if (
    pm?.riskLevel === "HIGH" &&
    ((typeof move30d === "number" && move30d > 20) ||
      (typeof move7d === "number" && move7d > 8)) &&
    nearHighs
  ) {
    out.verdict = "⚠ WAIT — EXTENDED";
    out.verdictStyle = "warning";
    out.subtitle = normalizeSubtitle(out.verdict, "Extended — wait for pullback to EMA21");
    out.actionText = actionTextForVerdict(out.verdict);
    out.showCallsBadge = false;
    out.showPutsBadge = false;
    return finalizePositionState(out, inPosition, activeSignal);
  }

  // ── RULE 2 — Top / Bottom module signals ──
  if (moduleSignal === "TOP_CONFIRMED" || moduleSignal === "TOP_FORMING") {
    out.verdict = "⚠ TOP FORMING";
    out.verdictStyle = "bearish";
    out.subtitle = normalizeSubtitle(out.verdict, "Distribution detected — avoid longs");
    out.actionText = actionTextForVerdict(out.verdict);
    out.showCallsBadge = false;
    out.showPutsBadge = score != null && score <= -4;
    return finalizePositionState(out, inPosition, activeSignal);
  }

  if (moduleSignal === "BOTTOM_CONFIRMED") {
    out.verdict = "🔵 BOTTOM CONFIRMED";
    out.verdictStyle = "bullish";
    out.subtitle = normalizeSubtitle(out.verdict, "Reversal setup — watch for entry");
    out.actionText = actionTextForVerdict(out.verdict);
    out.showCallsBadge = false;
    out.showPutsBadge = false;
    return finalizePositionState(out, inPosition, activeSignal);
  }

  // ── RULE 3 — Score-based verdict ──
  if (score != null && score >= 6) {
    out.verdict = "⚡ CONSIDER CALLS";
    out.verdictStyle = "bullish";
    out.subtitle = normalizeSubtitle(out.verdict, "Setup confirmed — consider entry");
    out.actionText = actionTextForVerdict(out.verdict);
    out.showCallsBadge = true;
    out.showPutsBadge = false;
  } else if (score != null && score >= 3) {
    out.verdict = "👁 WATCH";
    out.verdictStyle = "neutral";
    out.subtitle = normalizeSubtitle(out.verdict, "Setup building — monitor");
    out.actionText = actionTextForVerdict(out.verdict);
    out.showCallsBadge = false;
    out.showPutsBadge = false;
  } else if (score != null && score <= -6) {
    out.verdict = "⚠ CONSIDER PUTS";
    out.verdictStyle = "bearish";
    out.subtitle = normalizeSubtitle(out.verdict, "Breakdown confirmed — consider puts");
    out.actionText = actionTextForVerdict(out.verdict);
    out.showCallsBadge = false;
    out.showPutsBadge = true;
  } else if (score != null && score <= -3) {
    out.verdict = "👁 WATCH — BEARISH";
    out.verdictStyle = "neutral";
    out.subtitle = normalizeSubtitle(out.verdict, "Weakness building — monitor");
    out.actionText = actionTextForVerdict(out.verdict);
    out.showCallsBadge = false;
    out.showPutsBadge = false;
  } else {
    out.verdict = "⚪ WAIT";
    out.verdictStyle = "neutral";
    out.subtitle = normalizeSubtitle(out.verdict, "No edge — stay in cash");
    out.actionText = actionTextForVerdict(out.verdict);
    out.showCallsBadge = false;
    out.showPutsBadge = false;
  }

  // ── RULE 3b — Extended at 52W highs + hot RSI despite strong score ──
  if (
    score != null &&
    score >= 6 &&
    out.verdict === "⚡ CONSIDER CALLS" &&
    typeof result.rsi === "number" &&
    result.rsi > 70 &&
    typeof result.range52w?.score === "number" &&
    result.range52w.score >= 80
  ) {
    out.verdict = "⚠ WAIT — EXTENDED";
    out.verdictStyle = "warning";
    out.subtitle = normalizeSubtitle(out.verdict, "Extended — RSI hot near 52W highs; wait for pullback");
    out.actionText = actionTextForVerdict(out.verdict);
    out.showCallsBadge = false;
  }

  return finalizePositionState(out, inPosition, activeSignal);
}

function finalizePositionState(out, inPosition, activeSignal) {
  out.showExitSignal = inPosition === true && activeSignal?.hard_exit_triggered === true;
  out.exitLabel = out.showExitSignal
    ? `⚠ EXIT SIGNAL — ${activeSignal?.hard_exit_reason || "Hard exit"}`
    : null;

  if (inPosition && activeSignal && !out.showExitSignal) {
    const days = tradingDaysBetween(activeSignal.signal_date, new Date());
    const dayN = (days ?? 0) + 1;
    out.holdingText = `HOLDING · Day ${dayN}`;
  } else {
    out.holdingText = null;
  }

  out.subtitle = normalizeSubtitle(out.verdict, out.subtitle);
  return out;
}

export function displayVerdictChipStyle(verdictStyle) {
  switch (verdictStyle) {
    case "bullish":
      return { color: "#16a34a", bg: "#16a34a18", border: "#16a34a55", bannerBg: "#16a34a", bannerText: "#fff" };
    case "bearish":
      return { color: "#ef4444", bg: "#ef444418", border: "#ef444455", bannerBg: "#ef4444", bannerText: "#fff" };
    case "warning":
      return { color: "#f59e0b", bg: "#f59e0b18", border: "#f59e0b55", bannerBg: "#d97706", bannerText: "#111827" };
    case "blocked":
      return { color: "#9ca3af", bg: "#37415133", border: "#4b556355", bannerBg: "#374151", bannerText: "#e5e7eb" };
    default:
      return { color: "#94a3b8", bg: "#2a2d4018", border: "#64748b55", bannerBg: "#2a2d40", bannerText: "#e2e8f0" };
  }
}

/** @deprecated Use resolveDisplayVerdict — kept for module panel references */
export function formatModuleSignalLabelFromDisplay(result, opts) {
  return resolveDisplayVerdict(result, opts).verdict;
}
